import {
	getCountryCodeForRegionCode,
	getExample,
	getSupportedRegionCodes,
	parsePhoneNumber
} from 'awesome-phonenumber';
import { t } from 'i18next';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';

import useLocale from 'hooks/useLocale';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { faCheckCircle, faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';

import EnumSelect from './EnumSelect';
import StringInput from './StringInput';
import { InputMessageContainer } from './styled/InputCommon.styled';
import * as S from './styled/PhoneNumber.styled';

/**
 * @author @buunsal
 * @description Phone Number Input Component
 * @param {Object} props - props of the typography component
 * @param {string} [props.name] - Naming so that the input can be called.
 * @param {string} [props.value] - The value entered in the input.
 * @param {function} [props.onChange] - The function that will be called when the input value changes.
 * @param {boolean} [props.disabled] - If true, enters the input disable mode.
 * @param {string} [props.successMessage] - The text that should appear in case of Success.
 * @param {any} [props.errorMessage] - The text that should appear in case of Error.
 * @param {string} [props.countryCode] - The country code of the phone number.
 * @returns {object} - Text object with the given props
 * @example
 	<PhoneNumber
		name="phoneNumber"
		value={phoneNumberWatcher}
		onChange={(e) => {
			setValue('phoneNumber', e);
		}}
	/>
 */

const PhoneNumber = (props) => {
	const {
		name,
		value,
		onChange,
		disabled,
		successMessage,
		errorMessage: errorMessageProp,
		countryCode: countryCodeProp
	} = props;

	const { Country } = useLocale();
	const [inputRef, setInputRef] = useState(null);

	const [selectedCountry, setSelectedCountry] = useState(countryCodeProp ?? Country);
	const [initNumber, setInitNumber] = useState(true);
	const countryCode = useMemo(() => getCountryCodeForRegionCode(selectedCountry), [selectedCountry]);

	const formMethods = useFormContext();
	const errorMessage = formMethods?.getFieldState?.(name, formMethods?.formState)?.error?.message ?? errorMessageProp;

	const convertNumberToMask = useCallback((value, selectedCountry, countryCode) => {
		const exampleNumber = getExample(selectedCountry, 'mobile');
		const numberMask = exampleNumber
			? exampleNumber.number.rfc3966
					.split('-')
					.filter((_, i) => i !== 0)
					.join(' ')
					.replaceAll(/\d/g, '_')
			: null;

		if (!value) return numberMask;

		const numberTemp = numberMask.split('');
		const significantNumbers = value?.split('')?.slice(countryCode?.toString().length ?? 0) ?? [];

		significantNumbers.forEach((num) => {
			numberTemp.some((c, index) => {
				if (c === '_') {
					numberTemp[index] = num;
					return true;
				}
			});
		});

		const newValue = numberTemp.join('');

		return newValue ? newValue : null;
	}, []);

	const convertMaskToNumber = useCallback(
		(value) => {
			if (!value) return null;

			const convertedValue = value.replaceAll(/\s|_/g, '');
			const newValue = convertedValue ? `${countryCode}${convertedValue}` : null;
			return newValue ? newValue : null;
		},
		[countryCode]
	);

	const [localValue, setLocalValue] = useState(convertNumberToMask(value, selectedCountry, countryCode));

	const [cursorPosition, setCursorPosition] = useState(0);

	const handleKeyDown = useCallback(
		(e) => {
			const cursorPos = inputRef.selectionStart;
			const selectionEnd = inputRef.selectionEnd;
			const numberTemp = localValue.split('');

			// if keys are numbers
			if (e.key.match(/\d/g)) {
				e.preventDefault();
				e.stopPropagation();

				if (cursorPos !== selectionEnd) {
					for (let i = cursorPos; i < selectionEnd; i += 1) {
						if (numberTemp[i] !== ' ') {
							numberTemp[i] = '_';
						}
					}
				}

				let newCursorPosition = cursorPos;

				if (cursorPos >= numberTemp.length) {
					if (numberTemp.includes('_')) {
						newCursorPosition = numberTemp.indexOf('_');
					} else {
						return;
					}
				}

				for (let i = 0; i < numberTemp.length; i += 1) {
					if ((i < cursorPos && numberTemp[i] === '_') || (i >= cursorPos && numberTemp[i] !== ' ')) {
						newCursorPosition = i;
						break;
					}
				}

				if (cursorPos !== selectionEnd) {
					numberTemp.splice(cursorPos, selectionEnd - cursorPos, e.key);
				} else {
					numberTemp.splice(newCursorPosition, 0, e.key);
					numberTemp.pop();
				}
				newCursorPosition += 1;

				for (let i = newCursorPosition; i < numberTemp.length; i += 1) {
					if (numberTemp[i] === ' ') {
						// slide the number to the right
						const temp = numberTemp[i];
						numberTemp[i] = numberTemp[i - 1];
						numberTemp[i - 1] = temp;
					}
				}

				setCursorPosition(newCursorPosition);
				inputRef.setSelectionRange(newCursorPosition, newCursorPosition);
				setInitNumber(false);
				setLocalValue(numberTemp.join(''));
			} else if (e.key === 'Backspace') {
				e.preventDefault();
				e.stopPropagation();

				if (cursorPos === 0 && cursorPos === selectionEnd) return;

				if (cursorPos !== selectionEnd) {
					for (let i = cursorPos; i < selectionEnd; i += 1) {
						const temp = numberTemp[i];
						if (temp !== ' ') {
							numberTemp[i] = '_';
						}
					}

					setCursorPosition(cursorPos);
					inputRef.setSelectionRange(cursorPos, cursorPos);
					setInitNumber(false);
					setLocalValue(numberTemp.join(''));
				} else {
					let newCursorPosition = cursorPos;

					for (let i = cursorPos - 1; i >= 0; i -= 1) {
						if (numberTemp[i] !== ' ') {
							newCursorPosition = i;
							break;
						}
					}

					numberTemp[newCursorPosition] = '_';
					setCursorPosition(newCursorPosition);
					inputRef.setSelectionRange(newCursorPosition, newCursorPosition);
					setInitNumber(false);
					setLocalValue(numberTemp.join(''));
				}
			} else if (e.key === 'Delete') {
				e.preventDefault();
				e.stopPropagation();

				if (cursorPos >= numberTemp.length) return;

				let newCursorPosition = cursorPos;

				for (let i = cursorPos; i < numberTemp.length; i += 1) {
					if (numberTemp[i] !== ' ') {
						newCursorPosition = i;
						break;
					}
				}

				numberTemp[newCursorPosition] = '_';
				setInitNumber(false);
				setLocalValue(numberTemp.join(''));
				setCursorPosition(cursorPos);
			} else if (
				e.key === 'ArrowLeft' ||
				e.key === 'ArrowRight' ||
				e.key === 'ArrowUp' ||
				e.key === 'ArrowDown' ||
				e.key === 'Tab' ||
				e.key === 'Enter' ||
				((e.ctrlKey || e.metaKey) && e.key === 'a') ||
				((e.ctrlKey || e.metaKey) && e.key === 'c') ||
				((e.ctrlKey || e.metaKey) && e.key === 'x') ||
				((e.ctrlKey || e.metaKey) && e.key === 'z') ||
				((e.ctrlKey || e.metaKey) && e.key === 'v')
			) {
				// do nothing
			} else {
				e.preventDefault();
				e.stopPropagation();
			}
		},
		[inputRef, localValue, setLocalValue, setCursorPosition]
	);

	const handlePaste = useCallback(
		(e) => {
			e.preventDefault();
			e.stopPropagation();

			const clipboardData = e.clipboardData || window.clipboardData;
			const pastedData = clipboardData.getData('Text');
			const cursorPos = inputRef.selectionStart;
			const numberTemp = localValue.split('');

			let newCursorPosition;

			const data = pastedData
				?.replaceAll(' ', '')
				.split('')
				.filter((num) => !isNaN(num) && num !== ' ');
			for (let i = 0; i < numberTemp.length; i += 1) {
				if ((i < cursorPos && numberTemp[i] === '_') || (i >= cursorPos && numberTemp[i] !== ' ')) {
					numberTemp[i] = data.shift();
					newCursorPosition = i + 1;

					if (data.length === 0) {
						break;
					}
				}
			}

			setCursorPosition(newCursorPosition);
			inputRef.setSelectionRange(newCursorPosition, newCursorPosition);
			setLocalValue(numberTemp.join(''));
		},
		[inputRef, localValue, setLocalValue, setCursorPosition]
	);

	const handleOnInput = useCallback(
		(e) => {
			// control for android chrome
			if (!navigator.userAgent.match(/Android/i) || !navigator.userAgent.match(/Chrome/i)) return;
			e.preventDefault();
			e.stopPropagation();

			const key = e.nativeEvent.data;

			if (key.match(/\d/g)) {
				const cursorPos = inputRef.selectionStart;
				let newCursorPosition = cursorPos;
				const numberTemp = localValue.split('');

				if (cursorPos >= numberTemp.length) {
					if (numberTemp.includes('_')) {
						newCursorPosition = numberTemp.indexOf('_');
					} else {
						return;
					}
				}

				for (let i = 0; i < numberTemp.length; i += 1) {
					if ((i < cursorPos && numberTemp[i] === '_') || (i >= cursorPos && numberTemp[i] !== ' ')) {
						newCursorPosition = i;
						break;
					}
				}

				numberTemp.splice(newCursorPosition, 0, key);
				numberTemp.pop();
				newCursorPosition += 1;

				for (let i = newCursorPosition; i < numberTemp.length; i += 1) {
					if (numberTemp[i] === ' ') {
						// slide the number to the right
						const temp = numberTemp[i];
						numberTemp[i] = numberTemp[i - 1];
						numberTemp[i - 1] = temp;
					}
				}

				setCursorPosition(newCursorPosition);
				inputRef.setSelectionRange(newCursorPosition, newCursorPosition);
				setInitNumber(false);
				setLocalValue(numberTemp.join(''));
			} else {
				const cursorPos = inputRef.selectionStart;
				let newCursorPosition = cursorPos;
				const numberTemp = localValue.split('');

				for (let i = 0; i < numberTemp.length; i += 1) {
					if ((i < cursorPos && numberTemp[i] === '_') || (i >= cursorPos && numberTemp[i] !== ' ')) {
						newCursorPosition = i;
						break;
					}
				}
				setCursorPosition(newCursorPosition);
				inputRef.setSelectionRange(newCursorPosition, newCursorPosition);
			}
		},
		[inputRef, localValue, setLocalValue, setCursorPosition, setInitNumber]
	);

	useUpdateEffect(() => {
		const newValue = convertMaskToNumber(localValue);

		if (newValue !== value) {
			onChange(newValue);
		}

		if (inputRef) {
			inputRef.setSelectionRange(cursorPosition, cursorPosition);
		}
	}, [localValue]);

	useEffect(() => {
		setInitNumber(true);
		if (value && initNumber) {
			const correctedValue = value
				.split('')
				.filter((num) => !isNaN(num) && num !== ' ')
				.join('');

			// Case: Try to auto detect country phone number length and country code
			if (selectedCountry) {
				const exampleNumber = getExample(selectedCountry, 'mobile');
				const countOfSignificantDigits = exampleNumber?.number?.significant.length ?? 0;
				const countOfDigits = correctedValue.toString().length;

				if (countOfDigits === countOfSignificantDigits) {
					setLocalValue(convertNumberToMask(`${countryCode}${correctedValue}`, selectedCountry, countryCode));
					return;
				}
			}

			// Case: If the value is a possible national phone number with country code
			const isPossible = correctedValue.startsWith(countryCode.toString());
			if (isPossible) {
				setLocalValue(convertNumberToMask(correctedValue, selectedCountry, countryCode));
				return;
			}

			// Case: If the value is possible international phone number
			const parsedNumber = parsePhoneNumber(`+${correctedValue}`);
			if (
				parsedNumber?.possible ||
				(parsedNumber?.possibility !== 'invalid-country-code' && parsedNumber?.regionCode)
			) {
				setSelectedCountry(parsedNumber.regionCode);
				setLocalValue(
					convertNumberToMask(
						correctedValue,
						parsedNumber.regionCode,
						getCountryCodeForRegionCode(parsedNumber.regionCode)
					)
				);
				return;
			}

			// Case: If the value is a possible national phone number
			const parsedNumber2 = parsePhoneNumber(correctedValue, {
				regionCode: selectedCountry
			});
			if (
				parsedNumber2?.possible ||
				(parsedNumber2?.possibility !== 'invalid-country-code' && parsedNumber2?.regionCode)
			) {
				setSelectedCountry(parsedNumber2.regionCode);
				setLocalValue(
					convertNumberToMask(
						parsedNumber2.number?.e164?.split('').slice(1).join(''),
						parsedNumber2.regionCode,
						getCountryCodeForRegionCode(parsedNumber2.regionCode)
					)
				);
				return;
			}

			// Case: If the value is a possible phone number with exact format
			const parsedNumber3 = parsePhoneNumber(correctedValue);
			if (
				parsedNumber3?.possible ||
				(parsedNumber3?.possibility !== 'invalid-country-code' && parsedNumber3?.regionCode)
			) {
				setSelectedCountry(parsedNumber3.regionCode);
				setLocalValue(
					convertNumberToMask(
						parsedNumber3.number?.e164?.split('').slice(1).join(''),
						parsedNumber3.regionCode,
						getCountryCodeForRegionCode(parsedNumber3.regionCode)
					)
				);
				return;
			}
			setLocalValue(convertNumberToMask(value, selectedCountry));
		} else if (value) {
			setLocalValue(convertNumberToMask(value, selectedCountry, countryCode));
		} else {
			setLocalValue(convertNumberToMask(value, selectedCountry));
		}
	}, [value]);

	const handleCountryCodeChange = useCallback(
		(value) => {
			onChange(null);
			const newValue = value ?? Country;
			setSelectedCountry(newValue);

			const exampleNumber = getExample(newValue, 'mobile');
			const numberMask = exampleNumber
				? exampleNumber.number.rfc3966
						.split('-')
						.filter((_, i) => i !== 0)
						.join(' ')
						.replaceAll(/\d/g, '_')
				: null;

			if (numberMask !== localValue) {
				setLocalValue(numberMask);
			}
		},
		[localValue]
	);

	useUpdateEffect(() => {
		handleCountryCodeChange(countryCodeProp);
	}, [countryCodeProp]);

	const handleInputChange = useCallback(
		(value) => {
			setInitNumber(false);
			setLocalValue(value);
		},
		[setLocalValue, setInitNumber]
	);

	const handleFocus = useCallback(() => {
		const numberTemp = localValue.split('');
		const cursorPos = inputRef.selectionStart;
		const selectionEnd = inputRef.selectionEnd;
		if (cursorPos !== selectionEnd) return;

		let newCursorPosition = numberTemp.length;

		for (let i = 0; i < numberTemp.length; i += 1) {
			if (numberTemp[i] === '_') {
				newCursorPosition = i;
				break;
			}
		}

		if (cursorPos > newCursorPosition) {
			inputRef.setSelectionRange(newCursorPosition, newCursorPosition);
		}
	}, [inputRef, localValue]);

	return (
		<>
			<S.PhoneNumberContainer>
				<EnumSelect
					value={selectedCountry}
					setValue={handleCountryCodeChange}
					data={getSupportedRegionCodes()}
					renderSelectionValue={(value) => (
						<S.OptionContainer className="selected">
							<span className={`oto-country-flag flag-${value?.toLowerCase()}`} />
							<Typography>{` +${getCountryCodeForRegionCode(value)}`}</Typography>
						</S.OptionContainer>
					)}
					renderOption={(value) => (
						<S.OptionContainer>
							<span className={`oto-country-flag flag-${value?.toLowerCase()}`} />
							<Typography>{`country.${value}`}</Typography>
							<Typography fontWeight="700">{` +${getCountryCodeForRegionCode(value)}`}</Typography>
						</S.OptionContainer>
					)}
					searchFunction={(value, searchValue) =>
						t(`country.${value}`)?.toLowerCase().includes(searchValue.toLowerCase()) ||
						`+${getCountryCodeForRegionCode(value)}`.includes(searchValue) ||
						value?.toLowerCase().includes(searchValue.toLowerCase())
					}
					valueRemovable={false}
					fixedContainerWidth={false}
					disabled={disabled}
					error={!!errorMessage}
				/>
				<S.InputContainer>
					<StringInput
						ref={setInputRef}
						value={localValue}
						setValue={handleInputChange}
						disabled={disabled}
						onKeyDown={handleKeyDown}
						onBeforeInput={handleOnInput}
						onPaste={handlePaste}
						onClick={handleFocus}
						type="tel"
						className={errorMessage ? 'error' : ''}
					/>
				</S.InputContainer>
			</S.PhoneNumberContainer>
			<Alerts errorMessage={errorMessage} successMessage={successMessage} />
		</>
	);
};

const Alerts = ({ successMessage, errorMessage }) => (
	<>
		{successMessage && (
			<InputMessageContainer className="string-input-success-message-container">
				<Icon icon={faCheckCircle} color="var(--green)" size="x-small" />
				<Typography variant="caption" fontWeight="500" color="var(--green)" noWrap>
					{successMessage}
				</Typography>
			</InputMessageContainer>
		)}
		{errorMessage && (
			<InputMessageContainer className="error-message-container string-input-error-message-container">
				<Icon icon={faExclamationCircle} color="var(--red)" size="x-small" />
				<Typography variant="caption" fontWeight="500" color="var(--red)" noWrap>
					{errorMessage}
				</Typography>
			</InputMessageContainer>
		)}
	</>
);

export default PhoneNumber;
